import { ORPCError } from '@orpc/client';
import enLocale from './locale/en.json';

/**
 * `<input type="number">` hands react-hook-form a string, and an empty input hands it `''`.
 * `z.coerce.number()` would read `''` as `0`, so blanks are turned back into `undefined` before the
 * resolver ever sees them — an untouched optional field must stay absent, not become zero.
 */
export const numberFieldOptions = {
  setValueAs: (value: unknown): number | undefined => {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }

    const parsed = Number(value);

    return Number.isNaN(parsed) ? undefined : parsed;
  },
} as const;

/** Same idea for optional text: an empty input means "not set", not an empty string. */
export const textFieldOptions = {
  setValueAs: (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
      return value === null || value === undefined ? undefined : String(value);
    }

    const trimmed = value.trim();

    return trimmed === '' ? undefined : trimmed;
  },
} as const;

/** Optional text that has to be clearable on a PATCH: an emptied input sends an explicit `null`. */
export const clearableTextFieldOptions = {
  setValueAs: (value: unknown): string | null => {
    if (typeof value !== 'string') {
      return value === null || value === undefined ? null : String(value);
    }

    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
  },
} as const;

/** Just enough of a zod schema to ask it a question, so this module keeps no dependency on zod. */
interface ValueSchema {
  safeParse: (value: unknown) => { success: boolean };
}

/**
 * Does this number fit the contract schema that will receive it (plan §2.5)?
 *
 * `<input type="number">` will hand over anything that can be typed — `2` on the way to `2026`, a
 * fractional odometer, one digit too many on a price — and the oRPC input schema rejects it *before*
 * the handler, as an "Input validation failed" nobody can act on. The forms ask the **contract's own
 * schema** rather than repeating its ranges, so a range can only be changed in one place. `null` is the
 * blank field, which is always allowed to reach an optional column.
 */
export const fitsSchema = (schema: ValueSchema, value: number | null): boolean => value === null || schema.safeParse(value).success;

export const isConflictError = (error: unknown): boolean => error instanceof ORPCError && error.status === 409;

export const isForbiddenError = (error: unknown): boolean => error instanceof ORPCError && error.status === 403;

/**
 * A refusal, as opposed to a bad moment. The distinction is what a retry may act on: a `4xx` says the
 * request itself is wrong and will be just as wrong in four seconds' time, while a network failure or a
 * `5xx` is worth trying again on its own.
 */
export const isRefusedError = (error: unknown): boolean => error instanceof ORPCError && error.status >= 400 && error.status < 500;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Does the web have wording of its own for this `reasonKey`?
 *
 * The rule used to be an allowlist of three keys, which left the other 57 refusals reaching an Arabic
 * screen as the service's English sentence (plan §2.7). It is now the other way round: **any** key the
 * locale resolves is translated, and the server's sentence is the fallback for one that is not.
 *
 * `en.json` is the reference rather than the active language — `scripts/check-locales.ts` fails the
 * build unless `ar.json` carries every key it does, so "exists in English" is "exists in both", and the
 * check stays a pure function of the bundle instead of depending on the initialised i18next instance
 * (which would drag the DOM into this module and into its spec).
 */
export const hasTranslation = (key: string): boolean => {
  let node: unknown = enLocale;

  for (const part of key.split('.')) {
    if (!isRecord(node)) {
      return false;
    }

    node = node[part];
  }

  return typeof node === 'string' && node.trim() !== '';
};

/**
 * The refusal the service sent, wherever the client left it.
 *
 * A refusal oRPC serialised itself arrives as `{ message, reasonKey, reasons }` in `error.data`. One the
 * client could not recognise as oRPC-shaped — anything answered by a plain Nest exception filter, or by
 * a proxy in front of the API — is wrapped whole, and the payload sits one level down in
 * `error.data.body`. Both are read, so the page says the same thing either way.
 */
const refusalPayloads = (error: unknown): Record<string, unknown>[] => {
  if (!(error instanceof ORPCError) || !isRecord(error.data)) {
    return [];
  }

  return isRecord(error.data.body) ? [error.data, error.data.body] : [error.data];
};

/** The first payload that carries `field` as a sentence; a blank one is treated as absent. */
const refusalText = (error: unknown, field: string): string | null => {
  for (const payload of refusalPayloads(error)) {
    const value = payload[field];

    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }

  return null;
};

/** The `reasonKey` the service names its refusal with, when it sent one. */
const reasonKeyOf = (error: unknown): string | null => refusalText(error, 'reasonKey');

/**
 * Turns a failed mutation into the right i18n key. A named reason the web can translate wins — a
 * forced password change is a 403 that has nothing to do with the caller's role — then a 409 and a
 * 403, the two outcomes a user can act on; everything else is the generic save failure.
 */
export const submitErrorKey = (error: unknown): string => {
  const reasonKey = reasonKeyOf(error);

  if (reasonKey !== null && hasTranslation(reasonKey)) {
    return reasonKey;
  }

  if (isConflictError(error)) {
    return 'errors.conflict';
  }

  if (isForbiddenError(error)) {
    return 'errors.adminOnly';
  }

  return 'errors.saveFailed';
};

/**
 * The rule keys a refusal names (plan §5.6). A `400` from a rule carries `reasons: string[]` — every
 * blocker at once, so one round trip lists everything to fix — and the page translates each of them
 * rather than printing the service's English sentence twice.
 */
export const refusalReasons = (error: unknown): string[] => {
  for (const payload of refusalPayloads(error)) {
    if (Array.isArray(payload.reasons)) {
      return payload.reasons.filter((reason): reason is string => typeof reason === 'string');
    }
  }

  return [];
};

/** Prefers the service's own message (the 409s name the offending row) over the generic fallback. */
export const serverMessage = (error: unknown): string | null => {
  if (!(error instanceof ORPCError)) {
    return null;
  }

  const reasonKey = reasonKeyOf(error);

  // A refusal the web translates has to reach `submitErrorKey`; returning the service's English here
  // would print it untranslated next to an Arabic form (plan §11 fix 2).
  if (reasonKey !== null && hasTranslation(reasonKey)) {
    return null;
  }

  const message = refusalText(error, 'message');

  if (message !== null) {
    return message;
  }

  return typeof error.message === 'string' && error.message.trim() !== '' ? error.message : null;
};
